import React, {useCallback, useEffect, useRef, useState} from 'react';
import {getErrors, SchemaDialogProps} from './CommonRequestDialog';
import {api, ApiContext, Data, useApiContext} from '../../api';
import {Params} from '../../types/Params';
import {downloadByPost} from '../../util';
import {ResponseMessage} from '../../components/RequestForm';
import {Actions} from '../../types/Action';
import {Errors} from '../../types/Errors';
import {Dialog} from '../Dialog';
import {ItemForm, OnSaveFunc} from '../../components/ItemForm';
import styled from 'styled-components';
import {selectComponent} from '../../components/util';
import {ItemDetailsProps} from '../../components/ItemDetails';
import {extractSections} from '../Schema';
import {Spinner} from '../Spinner';

type Props = {
  shown: boolean;
  args: ExportArgs;
  onClose: (resp?: ResponseMessage) => void;
  actions: Actions;
};

export type ExportArgs = {
  appId: string;
  path: string;
  params: Params;
};

type Status = 'init' | 'exporting' | 'exported';

function isDownloading(appId: string): boolean {
  return document.cookie
    .split(';')
    .some((item) => item === 'downloading=' + appId);
}

export function ExportDialog({
  shown,
  args,
  onClose,
  actions,
}: Props): JSX.Element | null {
  const ctx = useApiContext();
  const ref = useRef<Dialog>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [dialogProps, setDialogProps] = useState<SchemaDialogProps>();
  const [status, setStatus] = useState<Status>('init');

  const doClose = useCallback(() => {
    if (ref && ref.current) {
      ref.current.closeDialog();
    }
  }, []);

  useEffect(() => {
    if (shown && ref && ref.current) {
      ref.current.showDialog();
    }
  }, [shown]);

  useEffect(() => {
    if (status === 'exporting') {
      const intervalId = setInterval(() => {
        if (!isDownloading(args.appId)) {
          clearInterval(intervalId);
          setStatus('exported');
          doClose();
        }
      }, 100);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [args.appId, status, doClose]);

  const doCheck = useCallback(
    async (params: Params) => {
      try {
        await api.postJson(
          ctx,
          `/app/${args.appId}/${args.path}/check`,
          params,
        );
      } catch (e) {
        setErrors(getErrors(e));
        throw e;
      }
    },
    [args.appId, args.path, ctx],
  );

  const doExport = useCallback(
    async (params: Params) => {
      setStatus('exporting');

      try {
        await doCheck(params);
      } catch (e) {
        return;
      }

      downloadByPost(`/app/${args.appId}/${args.path}`, params);
    },
    [args.appId, args.path, doCheck],
  );

  const onOpen = useCallback(async () => {
    try {
      const props = await api.postJson(
        ctx,
        `/app/${args.appId}/${args.path}/new`,
        args.params,
      );

      if (props !== null) {
        setDialogProps(props);
        return;
      }

      await doExport(args.params);
    } catch (e) {
      setErrors(getErrors(e));
      return false;
    }
  }, [ctx, args.appId, args.path, args.params, doExport]);

  const onSave = useCallback(
    async (data?: Data) => {
      try {
        await doExport({
          ...args.params,
          ...data,
        });
        setErrors({});
        return true;
      } catch (e) {
        setErrors(getErrors(e));
        throw e;
      }
    },
    [args.params, doExport],
  );

  const close = useCallback(() => {
    setDialogProps(undefined);
    setErrors({});
    onClose();
  }, [onClose]);

  return (
    <Dialog
      ref={ref}
      maxWidth={'calc(100vw - 100px)'}
      onOpen={onOpen}
      title={dialogProps?.title}
      onClose={close}
      errors={errors['_']}>
      <MessageContainer>{dialogProps?.message}</MessageContainer>
      <Contents
        ctx={ctx}
        dialogProps={dialogProps}
        onSave={onSave}
        onCancel={doClose}
        actions={actions}
      />
    </Dialog>
  );
}

type ContentsProps = {
  ctx: ApiContext;
  dialogProps?: SchemaDialogProps;
  onSave: OnSaveFunc;
  onCancel: () => void;
  actions: Actions;
};

function Contents({
  ctx,
  dialogProps,
  onSave,
  onCancel,
  actions,
}: ContentsProps): JSX.Element | null {
  if (!dialogProps) {
    return <Spinner />;
  }

  const res = dialogProps.resource;
  const props = selectComponent<ItemDetailsProps>(
    res.schema.screen.components,
    'item',
  );

  if (!props) {
    return null;
  }

  const sections = extractSections(res.schema);

  return (
    <ItemForm
      ctx={ctx}
      resource={dialogProps.resource}
      sections={sections}
      labelPosition={props.labelPosition ?? 'top'}
      fieldSeparator={props.fieldSeparator ?? true}
      onCancel={onCancel}
      onSave={onSave}
      actions={actions}
      saveButtonLabel={dialogProps.executeButtonLabel}
    />
  );
}

const MessageContainer = styled.div`
  line-height: 1.5;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  white-space: pre;
`;
